import EntityStatus from "../components/EntityStatus";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import EmployeeSummary from "../components/EmployeeSummary";
import EpisodesTable from "../components/EpisodesTable";
import PageSectionCard from "../components/PageSectionCard";
import { useAppSession } from "../context/AppSessionContext";
import { isActiveEpisode, toLocalDateKey } from "../lib/episodeStatus";
import { readErrorMessage } from "../lib/http";
import { Employee, getEmployeeClientCompany } from "../types/employees";
import { Episode } from "../types/incapacity";
import { PaginatedResponse } from "../types/pagination";

const episodeHistoryLimit = 50;

const byStartDateDesc = (a: Episode, b: Episode) =>
  new Date(b.startDate).getTime() - new Date(a.startDate).getTime();

function EmployeeDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { authorizedFetch, canMaintainEmployees } = useAppSession();

  const [employee, setEmployee] = useState<Employee | null>(null);
  const [isLoadingEmployee, setIsLoadingEmployee] = useState(true);
  const [employeeError, setEmployeeError] = useState<string | null>(null);

  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [isLoadingEpisodes, setIsLoadingEpisodes] = useState(true);
  const [episodesError, setEpisodesError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setEmployeeError(t("Missing employee ID in the route."));
      setIsLoadingEmployee(false);
      return;
    }

    let cancelled = false;

    const loadEmployee = async () => {
      setIsLoadingEmployee(true);
      setEmployeeError(null);

      try {
        const response = await authorizedFetch(`/employees/${id}`);

        if (response.status === 404) {
          throw new Error(t("Employee was not found."));
        }

        if (!response.ok) {
          throw new Error(
            await readErrorMessage(
              response,
              t("Failed to load employee ({{status}}).", {
                status: response.status,
              }),
            ),
          );
        }

        const payload = (await response.json()) as Employee;

        if (!cancelled) {
          setEmployee(payload);
        }
      } catch (error) {
        if (!cancelled) {
          setEmployeeError(
            error instanceof Error
              ? error.message
              : t("Failed to load employee."),
          );
          setEmployee(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingEmployee(false);
        }
      }
    };

    void loadEmployee();

    return () => {
      cancelled = true;
    };
  }, [authorizedFetch, id, t]);

  useEffect(() => {
    if (!id) {
      return;
    }

    let cancelled = false;

    const loadEpisodes = async () => {
      setIsLoadingEpisodes(true);
      setEpisodesError(null);

      try {
        const query = new URLSearchParams({
          employeeId: id,
          page: "1",
          limit: String(episodeHistoryLimit),
        });
        const response = await authorizedFetch(`/episodes?${query.toString()}`);

        if (!response.ok) {
          throw new Error(
            await readErrorMessage(
              response,
              t("Failed to load recent episodes ({{status}}).", {
                status: response.status,
              }),
            ),
          );
        }

        const payload = (await response.json()) as PaginatedResponse<Episode>;

        if (!cancelled) {
          setEpisodes(payload.data ?? []);
        }
      } catch (error) {
        if (!cancelled) {
          setEpisodesError(
            error instanceof Error
              ? error.message
              : t("Failed to load recent episodes."),
          );
          setEpisodes([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingEpisodes(false);
        }
      }
    };

    void loadEpisodes();

    return () => {
      cancelled = true;
    };
  }, [authorizedFetch, id, t]);

  const { ongoingEpisodes, recentEpisodes } = useMemo(() => {
    const todayKey = toLocalDateKey(new Date());
    const sorted = [...episodes].sort(byStartDateDesc);

    return {
      ongoingEpisodes: sorted.filter((episode) =>
        isActiveEpisode(episode, todayKey),
      ),
      recentEpisodes: sorted.filter(
        (episode) => !isActiveEpisode(episode, todayKey),
      ),
    };
  }, [episodes]);

  if (isLoadingEmployee) {
    return (
      <PageSectionCard
        titleId="employee-detail-title"
        eyebrow={t("Employee")}
        title={t("Loading employee...")}
      >
        <p>{t("Loading employee details...")}</p>
      </PageSectionCard>
    );
  }

  if (employeeError || !employee) {
    return (
      <PageSectionCard
        titleId="employee-detail-title"
        eyebrow={t("Employee")}
        title={t("Employee")}
      >
        <p className="errorMessage" role="alert">
          {employeeError ?? t("Employee was not found.")}
        </p>
        <p>
          <Link className="secondaryButton" to="/intake">
            {t("Back to search")}
          </Link>
        </p>
      </PageSectionCard>
    );
  }

  const clientCompanyName = getEmployeeClientCompany(employee)?.name;

  return (
    <PageSectionCard
      titleId="employee-detail-title"
      eyebrow={t("Employee")}
      title={employee.fullName}
      lead={clientCompanyName ?? undefined}
      actions={
        canMaintainEmployees ? (
          <div className="headerActions">
            <Link
              className="secondaryButton"
              to={`/employees/${employee.id}/edit`}
            >
              {t("Edit")}
            </Link>
            {!employee.deletedAt &&
            !employee.workstation?.deletedAt &&
            !getEmployeeClientCompany(employee)?.deletedAt ? (
              <Link
                className="primaryButton"
                to={`/intake/new?employeeId=${employee.id}`}
              >
                {t("Create new episode")}
              </Link>
            ) : null}
            <EntityStatus deletedAt={employee.deletedAt} />
          </div>
        ) : undefined
      }
    >
      <div className="intakeFormLayout">
        <section className="subCard" aria-labelledby="employee-info-heading">
          <h3 id="employee-info-heading" className="subCardTitle">
            {t("Employee details")}
          </h3>
          <EmployeeSummary employee={employee} />
        </section>

        <section className="subCard" aria-labelledby="ongoing-episodes-heading">
          <h3 id="ongoing-episodes-heading" className="subCardTitle">
            {t("Ongoing episodes")}
          </h3>

          {isLoadingEpisodes ? (
            <p>{t("Loading recent episodes...")}</p>
          ) : episodesError ? (
            <p className="errorMessage" role="alert">
              {episodesError}
            </p>
          ) : (
            <EpisodesTable
              ariaLabel={t("Ongoing episodes")}
              episodes={ongoingEpisodes}
              emptyState={t("No ongoing episodes.")}
            />
          )}
        </section>

        <section className="subCard" aria-labelledby="recent-episodes-heading">
          <h3 id="recent-episodes-heading" className="subCardTitle">
            {t("Recent episodes")}
          </h3>

          {isLoadingEpisodes ? (
            <p>{t("Loading recent episodes...")}</p>
          ) : episodesError ? (
            <p className="errorMessage" role="alert">
              {episodesError}
            </p>
          ) : (
            <EpisodesTable
              ariaLabel={t("Recent episodes")}
              episodes={recentEpisodes}
              emptyState={t("No previous episodes found.")}
            />
          )}
        </section>
      </div>
    </PageSectionCard>
  );
}

export default EmployeeDetailPage;
